import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { NodeSpec } from './NodeSpec';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import { getISDUVariableOptions } from './isdu-variables';

export const isduWriteNodeSpec: NodeSpec<NodeCfg<'isduWrite'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'IO Link',
    requiredSockets: 'none',
    kindLabel: 'ISDU Write',
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.IoLinkMaster ],
            },
            on: {
                // the available variables come from the iodd of the device on the selected connection, so a different connection invalidates them
                'update:value': (connectionId: string, { flowEventEmitter }) => flowEventEmitter.emit('updateNodeCfg', {
                    newNodeCfg: {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            connectionId,
                            isduVariables: connectionId === nodeCfg.controlValues.connectionId ? nodeCfg.controlValues.isduVariables : {},
                        },
                    },
                }),
            },
            value: nodeCfg.controlValues.connectionId,
        }),
        channelNumber: defineControlOpts({
            cfg: { kind: 'number', label: 'Channel', min: 0, max: 4 },
            on: {
                'update:value': (channelNumber: number, { flowEventEmitter }) => flowEventEmitter.emit('updateNodeCfg', {
                    newNodeCfg: {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            channelNumber,
                            isduVariables: channelNumber === nodeCfg.controlValues.channelNumber ? nodeCfg.controlValues.isduVariables : {},
                        },
                    },
                }),
            },
            value: nodeCfg.controlValues.channelNumber,
        }),
        isduVariables: defineControlOpts({
            cfg: {
                kind: 'selectWithCallback',
                selectLabel: 'Add ISDU Variable',
                items: async () => {
                    const available = await getISDUVariableOptions({
                        connectionId: nodeCfg.controlValues.connectionId,
                        channelNumber: nodeCfg.controlValues.channelNumber,
                        access: 'w',
                    });
                    return Object.entries(available)
                        .filter(([ id ]) => !nodeCfg.controlValues.isduVariables[id]?.tracked)
                        .map(([ id, details ]) => ({ value: id, label: details.name }));
                },
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'select': async (value, { flowEventEmitter }) => {
                    const available = await getISDUVariableOptions({
                        connectionId: nodeCfg.controlValues.connectionId,
                        channelNumber: nodeCfg.controlValues.channelNumber,
                        access: 'w',
                    });
                    const selected = available[value as string];
                    if (!selected) {
                        return;
                    }
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                isduVariables: {
                                    ...nodeCfg.controlValues.isduVariables,
                                    [value as string]: { ...selected, tracked: true },
                                },
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.isduVariables,
        }),
    }),
    cfg: () => ({
        id: getUID(),
         kind: 'isduWrite',
         width: 220,
         controlValues: {
            connectionId: '',
            channelNumber: -1,
            isduVariables: {},
        },
    }),
    inputs: nodeCfg => {
        const inputs: ReturnType<NodeSpec<NodeCfg<'isduWrite'>>['inputs']> = {
            trigger: { socket: 'event', label: 'Write ISDU Variables' },
        };
        for (const [ id, value ] of Object.entries(nodeCfg.controlValues.isduVariables)) {
            if (value.tracked) {
                inputs[`input_${id}`] = {
                    socket: value.dataKind,
                    label: value.name,
                    controlOpts: defineControlOpts({
                        cfg: { kind: 'deletableSocket' },
                        value: null,
                        on: {
                            'update:value': () => {}, // no-op, this control does not use the update:value event
                            'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        isduVariables: {
                                            ...nodeCfg.controlValues.isduVariables,
                                            [id]: { ...value, tracked: false },
                                        },
                                    },
                                },
                                direction: 'input',
                                removalMethod: { kind: 'uniqueIdentifier', value: `input_${id}` },
                            }),
                        },
                    }),
                };
            }
        }
        return inputs;
    },
    outputs: () => ({}),
    validator: node => node.controlValues.connectionId ? true : { connectionId: 'Connection id is required' },
};
